import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PlusIcon, WrenchIcon, PhoneIcon, EnvelopeIcon, MapPinIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const empty = {
  name: "",
  contact_person: "",
  phone: "",
  email: "",
  address: "",
  specialties: "",
  notes: "",
};

export default function RepairShops() {
  const { user } = useAuth();
  const [shops, setShops] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await api.get("/repair-shops");
    setShops(data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/repair-shops/${editing.id}`, form);
      else await api.post("/repair-shops", form);
      toast.success(editing ? "Shop updated" : "Shop added");
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    try {
      await api.delete(`/repair-shops/${s.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <div data-testid="shops-page">
      <PageHeader eyebrow="Vendors" title="Repair Shops" subtitle="Outside shops you send trucks to for repairs you don't do in-house.">
        <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="add-shop-button">
          <PlusIcon size={16} weight="bold" className="mr-2" /> Add Shop
        </Button>
      </PageHeader>

      <div className="p-8">
        {shops.length === 0 ? (
          <div className="bg-white border border-slate-300 rounded-sm p-12 text-center">
            <WrenchIcon size={40} weight="thin" className="mx-auto text-slate-400 mb-2" />
            <div className="text-sm text-slate-600">No repair shops yet. Add one to assign outside repairs.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {shops.map((s) => (
              <div key={s.id} className="bg-white border border-slate-300 rounded-sm p-5" data-testid={`shop-card-${s.id}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 bg-amber-100 border border-amber-200 rounded-sm flex items-center justify-center">
                    <WrenchIcon size={20} weight="bold" className="text-amber-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg font-bold tracking-tight truncate">{s.name}</div>
                    {s.contact_person && <div className="font-mono text-xs text-slate-500">{s.contact_person}</div>}
                  </div>
                </div>
                {s.specialties && (
                  <div className="mb-3 flex flex-wrap gap-1">
                    {s.specialties.split(",").map((t) => t.trim()).filter(Boolean).map((t, i) => (
                      <span key={`${s.id}-spec-${t}-${i}`} className="badge badge-info">{t}</span>
                    ))}
                  </div>
                )}
                <div className="space-y-1.5 text-sm">
                  {s.phone && (
                    <div className="text-slate-700 flex items-center gap-2"><PhoneIcon size={12} /> {s.phone}</div>
                  )}
                  {s.email && (
                    <div className="text-slate-700 flex items-center gap-2 truncate"><EnvelopeIcon size={12} /> {s.email}</div>
                  )}
                  {s.address && (
                    <div className="text-slate-700 flex items-start gap-2"><MapPinIcon size={12} className="mt-0.5 flex-shrink-0" /> <span className="text-xs">{s.address}</span></div>
                  )}
                </div>
                <div className="mt-4 pt-4 border-t border-slate-200 flex justify-end gap-3 text-xs">
                  <button onClick={() => { setEditing(s); setForm({ ...empty, ...s }); setOpen(true); }} className="text-blue-600 hover:underline font-semibold" data-testid={`edit-shop-${s.id}`}>Edit</button>
                  {user?.role === "admin" && (
                    <button onClick={() => del(s)} className="text-red-600 hover:underline font-semibold" data-testid={`del-shop-${s.id}`}>Delete</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-sm" data-testid="shop-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editing ? "Edit shop" : "Add repair shop"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <Field label="Shop Name *" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} tid="shop-name" />
            <Field label="Contact person" value={form.contact_person} onChange={(v) => setForm({ ...form, contact_person: v })} tid="shop-contact" />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} tid="shop-phone" />
              <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} tid="shop-email" />
            </div>
            <Field label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} tid="shop-address" />
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Specialties</Label>
              <Input value={form.specialties} onChange={(e) => setForm({ ...form, specialties: e.target.value })} placeholder="engine, transmission, tires" className="mt-1.5 rounded-sm" data-testid="shop-specialties" />
              <p className="text-[10px] text-slate-500 mt-1 font-mono">comma-separated</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 rounded-sm" data-testid="shop-notes" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="shop-save">{editing ? "Update" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required = false, tid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider font-bold text-slate-700">{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="mt-1.5 rounded-sm" data-testid={tid} />
    </div>
  );
}
